import { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { SceneCanvas } from "./components/SceneCanvas/SceneCanvas";
import { kaabaSceneConfig } from "./scene/kaaba";
import styles from "./App.module.css";
import { Leva } from "leva";
import { AppConfigPanel } from "./components/AppConfigPanel/AppConfigPanel";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { CameraViewOffsetProvider } from "./context/CameraViewOffsetContext";
import { $appState, $orderedSightIds, $sightsStore } from "./state/appState";
import { useUnit } from "effector-react";
import { AssistantHud } from "./components/AssistantHud";
import { $sceneLoadingState } from "./state/sceneLoading";
import { SplashScreen } from "./components/SplashScreen/SplashScreen";
import { MenuOverlay } from "./components/MenuOverlay";
import { GlobalMap } from "./components/GlobalMap/GlobalMap";
import { $audioConfig, updateAudioConfig } from "./state/appConfig";
import { Button } from "./components/Button";
import MenuIcon from "./icons/menu.svg?react";
import VolumeOnIcon from "./icons/volume_on.svg?react";
import VolumeOffIcon from "./icons/volume_off.svg?react";
import { useI18n } from "./i18n";
import type { Sight } from "./types";
import { $onboardingVisible } from "./state/onboarding";
import { OnboardingOverlay } from "./components/OnboardingOverlay/OnboardingOverlay";
import { SceneProgressBar } from "./components/SceneProgressBar/SceneProgressBar";
import { CameraDebugOverlay } from "./components/CameraDebugOverlay/CameraDebugOverlay";

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const isKaabaPath = useCallback(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const normalizedPath = window.location.pathname.replace(/\/+$/, "");
    return normalizedPath === "/kaaba";
  }, []);
  const [sceneStarted, setSceneStarted] = useState(() => isKaabaPath());
  const { appState, sceneLoadingState, audioConfig, updateAudio, orderedSightIds, sights, onboardingVisible } =
    useUnit({
      appState: $appState,
      sceneLoadingState: $sceneLoadingState,
      audioConfig: $audioConfig,
      updateAudio: updateAudioConfig,
      orderedSightIds: $orderedSightIds,
      sights: $sightsStore,
      onboardingVisible: $onboardingVisible,
    });
  const i18n = useI18n();
  useKeyboardNavigation();
  const orderedSights = useMemo(
    () =>
      orderedSightIds
        .map((id) => sights[id])
        .filter((item): item is Sight => Boolean(item)),
    [orderedSightIds, sights]
  );

  const showDebugControls = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    return params.has("dev");
  }, []);

  const showCameraDebug = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    return params.has("camera-debug");
  }, []);

  const previewIsReady = sceneStarted && sceneLoadingState.status === "preview";
  const sceneIsReady = sceneStarted && sceneLoadingState.status === "ready";

  const loadingMessage =
    sceneLoadingState.status === "error"
      ? i18n.app.loadError
      : i18n.app.loading;
  const loadedMb =
    sceneLoadingState.loadedBytes > 0
      ? sceneLoadingState.loadedBytes / (1024 * 1024)
      : 0;
  const totalMb =
    typeof sceneLoadingState.totalBytes === "number"
      ? sceneLoadingState.totalBytes / (1024 * 1024)
      : null;
  const progressText = totalMb
    ? `${loadedMb.toFixed(2)} / ${totalMb.toFixed(2)} ${i18n.app.progressUnitMb}`
    : `${loadedMb.toFixed(2)} ${i18n.app.progressUnitMb}`;

  const handleEnterScene = useCallback(() => {
    setSceneStarted(true);
    setMenuOpen(false);
    if (typeof window !== "undefined" && !isKaabaPath()) {
      window.history.pushState(null, "", "/kaaba");
    }
  }, [isKaabaPath]);

  const handleSoundToggle = useCallback(() => {
    updateAudio({ soundOn: !audioConfig.soundOn });
  }, [audioConfig.soundOn, updateAudio]);

  const handleReturnToMap = useCallback(() => {
    setSceneStarted(false);
    setMenuOpen(false);
    if (typeof window !== "undefined" && isKaabaPath()) {
      window.history.pushState(null, "", "/");
    }
  }, [isKaabaPath]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handlePopState = () => {
      setSceneStarted(isKaabaPath());
      setMenuOpen(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isKaabaPath]);

  return (
    <CameraViewOffsetProvider enabled={previewIsReady || sceneIsReady}>
      <div className={styles.app}>
        {sceneIsReady && (
          <Button
            className={styles.menuButton}
            aria-label={i18n.suggestions.openMenu}
            onClick={() => setMenuOpen(true)}
          >
            <MenuIcon />
          </Button>
        )}
        {/* <Button
          className={styles.soundToggle}
          onClick={handleSoundToggle}
          aria-label={
            audioConfig.soundOn ? i18n.app.muteSound : i18n.app.enableSound
          }
        >
          {audioConfig.soundOn ? (
            <VolumeOnIcon width={16} height={16} />
          ) : (
            <VolumeOffIcon width={16} height={16} />
          )}
        </Button> */}
        {!sceneStarted ? (
          <div className={styles.menuLayer}>
            <GlobalMap
              className={styles.menuMap}
              onEnterScene={handleEnterScene}
            />
            {showDebugControls && (
              <>
                <div dir="ltr">
                  <Leva collapsed titleBar={{ title: "Pilgrim Config" }} />
                  <AppConfigPanel />
                </div>
              </>
            )}
          </div>
        ) : (
          <div className={styles.sceneLayer}>
            <div
              className={cx(
                styles.sceneContent,
                (previewIsReady || sceneIsReady) && styles.sceneContentReady,
              )}
            >
              {orderedSights.length > 0 && (
                <SceneCanvas sights={orderedSights} config={kaabaSceneConfig} />
              )}
              {sceneIsReady && (
                <AssistantHud />
              )}
              {showDebugControls && (
                <>
                  <div dir="ltr">
                    <Leva collapsed titleBar={{ title: "Pilgrim Config" }} />
                    <AppConfigPanel />
                  </div>
                </>
              )}
            </div>
            <SplashScreen
              visible={false && !(previewIsReady || sceneIsReady)}
              message={loadingMessage}
              progressText={progressText}
            />
            <SceneProgressBar
              visible={previewIsReady && sceneLoadingState.splatPhase !== 'idle'}
              splatLoadedBytes={sceneLoadingState.splatLoadedBytes}
              splatTotalBytes={sceneLoadingState.splatTotalBytes}
              splatPhase={sceneLoadingState.splatPhase}
            />
            <MenuOverlay
              open={sceneIsReady && menuOpen}
              onClose={() => setMenuOpen(false)}
              onReturnToMap={handleReturnToMap}
            />
            <OnboardingOverlay visible={onboardingVisible && !menuOpen} />
            {showCameraDebug && <CameraDebugOverlay />}
          </div>
        )}
      </div>
    </CameraViewOffsetProvider>
  );
}

export default App;
